import { type ReactNode, useEffect, useRef } from 'react'
import type { Buchungstyp, Konto, KontoArt } from '../api'

/** Eingang bucht von Aktiva nach Passiva, Ausgang von Passiva nach Aktiva. */
export function kontoArten(typ: Buchungstyp): { konto: KontoArt; gegenkonto: KontoArt } {
  return typ === 'EINGANG' ? { konto: 'AKTIVA', gegenkonto: 'PASSIVA' } : { konto: 'PASSIVA', gegenkonto: 'AKTIVA' }
}

/** Setzt den Buchungstyp und leert Konten, die nicht mehr zur Richtung passen. */
export function mitTyp<T extends { kontoId: number | null; gegenkontoId: number | null }>(
  eingabe: T,
  typFeld: keyof T,
  typ: Buchungstyp,
  konten: Konto[],
): T {
  const arten = kontoArten(typ)
  const passt = (id: number | null, art: KontoArt) => id == null || konten.find((k) => k.id === id)?.art === art
  return {
    ...eingabe,
    [typFeld]: typ,
    kontoId: passt(eingabe.kontoId, arten.konto) ? eingabe.kontoId : null,
    gegenkontoId: passt(eingabe.gegenkontoId, arten.gegenkonto) ? eingabe.gegenkontoId : null,
  }
}

export function KontoAuswahl({
  konten,
  wert,
  onChange,
  art,
  leerText = '– kein Konto –',
}: {
  konten: Konto[]
  wert: number | null
  onChange: (id: number | null) => void
  /** nur Konten dieser Art anbieten (ein bereits gewähltes Konto bleibt sichtbar) */
  art?: KontoArt
  leerText?: string
}) {
  const sichtbar = konten.filter((k) => art == null || k.art === art || k.id === wert)
  const gruppe = (gruppenArt: KontoArt, titel: string) => {
    const eintraege = sichtbar.filter((k) => k.art === gruppenArt)
    return (
      eintraege.length > 0 && (
        <optgroup label={titel}>
          {eintraege.map((k) => (
            <option key={k.id} value={k.id}>
              {k.bezeichnung}
            </option>
          ))}
        </optgroup>
      )
    )
  }
  return (
    <select value={wert ?? ''} onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}>
      <option value="">{leerText}</option>
      {gruppe('AKTIVA', 'Aktiva')}
      {gruppe('PASSIVA', 'Passiva')}
    </select>
  )
}

export function Dialog({ offen, titel, onSchliessen, children }: { offen: boolean; titel: string; onSchliessen: () => void; children: ReactNode }) {
  const ref = useRef<HTMLDialogElement>(null)
  useEffect(() => {
    const d = ref.current
    if (!d) return
    if (offen && !d.open) d.showModal()
    if (!offen && d.open) d.close()
  }, [offen])
  return (
    <dialog ref={ref} onClose={onSchliessen}>
      <header>
        <h2>{titel}</h2>
        <button className="link" onClick={onSchliessen} aria-label="Schließen">
          ✕
        </button>
      </header>
      {offen && children}
    </dialog>
  )
}

export function Fehlermeldung({ fehler }: { fehler: string | null }) {
  return fehler ? <p className="fehler">{fehler}</p> : null
}

export function fehlerText(e: unknown): string {
  return e instanceof Error ? e.message : String(e)
}
